import React, { useEffect, useState } from 'react';
import { Layout, Tabs, Form, Input, Button, Select, Upload, InputNumber, Typography, message } from 'antd';
import { UploadOutlined,IdcardOutlined,UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const { TabPane } = Tabs;
const { TextArea } = Input;

const PropertyForm = () => {
  const [activeTab, setActiveTab] = useState('residential');
  const [residentialImages, setResidentialImages] = useState([]);
  const [commercialImages, setCommercialImages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subCities, setSubCities] = useState([]);
  const [commissionPercentage, setCommissionPercentage] = useState(0);
  const [selectedCity, setSelectedCity] = useState('');
  const navigate = useNavigate();
  const hosturl = 'https://api.bookarenthouse.com';

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${hosturl}/locations`);
        setLocations(response.data);
      } catch (error) {
        message.error('Failed to fetch locations.');
      }
    };

    const fetchCommission = async () => {
      try {
        const response = await axios.get(`${hosturl}/api/commissions`);
        if (response.data && response.data.length > 0) {
          setCommissionPercentage(response.data[0].commissionPercentage);
        }
      } catch (error) {
        message.error('Failed to fetch commission data.');
      }
    };

    fetchLocations();
    fetchCommission();
  }, []);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleUploadChange = (info, setImages) => {
    setImages(info.fileList);
    if (info.file.status === 'done') {
      message.success(`${info.file.name} file uploaded successfully.`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const handleCityChange = (value) => {
    const selectedLocation = locations.find(location => location.city === value);
    setSelectedCity(value);
    setSubCities(selectedLocation ? selectedLocation.subCities : []);
  };

  const handleSubmit = async (values, propertyType) => {
    const formData = new FormData();
    const images = propertyType === 'residential' ? residentialImages : commercialImages;
  
    images.forEach(file => {
      if (file.originFileObj) {
        formData.append(propertyType === 'residential' ? 'photos[]' : 'com-photos[]', file.originFileObj);
      }
    });
  
    Object.keys(values).forEach(key => {
      formData.append(key, values[key]);
    });
  
    // Calculate the commission amount
    const commissionAmount = values.rent * (commissionPercentage / 100);
    
    formData.append('commissionAmount', commissionAmount);
  
    try {
      const response = await axios.post(`${hosturl}/submit-${propertyType}`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
  
      if (response.status === 200) {
        const propertyId = response.data.propertyId; // Capture the property ID
        message.success(`${propertyType.charAt(0).toUpperCase() + propertyType.slice(1)} property details submitted successfully!`);
        navigate('/paymentgateway', { state: { amount: commissionAmount, propertyId } });
        
        // Resetting state and form fields
        setResidentialImages([]);
        setCommercialImages([]);
      }
    } catch (error) {
      console.error('Submission error:', error);
      message.error(`An error occurred: ${error.response ? error.response.data.message : error.message}`);
    }
  };
  
  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <p style={{ fontSize: '22px', fontWeight: 'bold', textAlign: 'center' }}>You are a</p>
                    <div className="button-center" style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                        <Button
                            type="default"
                            shape="round"
                            size="large"
                            onClick={() => navigate('/propertyform')}
                            icon={<IdcardOutlined />}
                            className="owner-button"
                        >
                            Owner
                        </Button>
                        <Button
                            type="default"
                            shape="round"
                            size="large"
                            onClick={() => navigate('/tenant')}
                            icon={<UserOutlined />}
                            style={{ marginLeft: '10px' }}
                        >
                            Tenant
                        </Button>
                    </div>
      <Layout.Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
      
        <div style={{ width: '800px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', padding: '20px' }}>
          <Tabs activeKey={activeTab} onChange={handleTabChange} style={{ marginBottom: '20px' }}>
            <TabPane tab="Residential" key="residential">
              <Form onFinish={(values) => handleSubmit(values, 'residential')} layout="vertical">
                <Form.Item name="location" label="Location" rules={[{ required: true, message: 'Location is required' }]}>
                  <Select onChange={handleCityChange} placeholder="Select a city" style={{ width: '100%' }}>
                    {locations.map(location => (
                      <Select.Option key={location._id} value={location.city}>
                        {location.city}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="subCity" label="Sub-City" rules={[{ required: true, message: 'Sub-City is required' }]}>
                  <Select placeholder="Select a sub-city" style={{ width: '100%' }}>
                    {subCities.map((subCity, index) => (
                      <Select.Option key={index} value={subCity}>
                        {subCity}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="rent" label="Rent" rules={[{ required: true, message: 'Rent is required' }]}>
                  <InputNumber placeholder="Enter rent" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="size" label="Size of Property" rules={[{ required: true, message: 'Size is required' }]}>
                  <Select placeholder="Select size" style={{ width: '100%' }}>
                    <Select.Option value="1Rk">1 RK</Select.Option>
                    <Select.Option value="1Bhk">1 BHK</Select.Option>
                    <Select.Option value="2Bhk">2 BHK</Select.Option>
                    <Select.Option value="3Bhk">3 BHK</Select.Option>
                    <Select.Option value="DuplexHouse">Duplex House</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="area" label="Area of Property (Sft)" rules={[{ required: true, message: 'Area is required' }]}>
                  <InputNumber placeholder="Enter area in Sft" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="face" label="Face of Property" rules={[{ required: true, message: 'Face is required' }]}>
                  <Select placeholder="Select face" style={{ width: '100%' }}>
                    <Select.Option value="East">East</Select.Option>
                    <Select.Option value="West">West</Select.Option>
                    <Select.Option value="North">North</Select.Option>
                    <Select.Option value="South">South</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="building" label="Building" rules={[{ required: true, message: 'Building type is required' }]}>
                  <Select placeholder="Select building type" style={{ width: '100%' }}>
                    <Select.Option value="Independent">Independent</Select.Option>
                    <Select.Option value="Apartment">Apartment (Floor)</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="buildingDetails" label="Building Details (optional)">
                  <Input placeholder="Optional" />
                </Form.Item>
                <Form.Item name="advance" label="Advance" rules={[{ required: true, message: 'Advance amount is required' }]}>
                  <InputNumber placeholder="Enter advance amount" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="Photos" required>
                  <Upload
                    listType="picture"
                    beforeUpload={() => false}
                    onChange={(info) => handleUploadChange(info, setResidentialImages)}
                    showUploadList={{ showPreviewIcon: true }}
                  >
                    <Button icon={<UploadOutlined />}>Add Images</Button>
                  </Upload>
                </Form.Item>
                <Form.Item name="innerFacilities" label="Inner Facilities" rules={[{ required: true, message: 'Inner facilities description is required' }]}>
                  <TextArea placeholder="Describe inner facilities" rows={4} />
                </Form.Item>
                <Form.Item name="nearbyFacilities" label="Nearby Facilities">
                  <TextArea placeholder="Describe nearby facilities" rows={4} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: '100%' }}>Submit</Button>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab="Commercial" key="commercial">
              <Form onFinish={(values) => handleSubmit(values, 'commercial')} layout="vertical">
              <Form.Item name="location" label="Location" rules={[{ required: true, message: 'Location is required' }]}>
                  <Select onChange={handleCityChange} placeholder="Select a city" style={{ width: '100%' }}>
                    {locations.map(location => (
                      <Select.Option key={location._id} value={location.city}>
                        {location.city}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="subCity" label="Sub-City" rules={[{ required: true, message: 'Sub-City is required' }]}>
                  <Select placeholder="Select a sub-city" style={{ width: '100%' }}>
                    {subCities.map((subCity, index) => (
                      <Select.Option key={index} value={subCity}>
                        {subCity}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="rent" label="Rent" rules={[{ required: true, message: 'Rent is required' }]}>
                  <InputNumber placeholder="Enter rent" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="area" label="Area of Property (Sft)" rules={[{ required: true, message: 'Area is required' }]}>
                  <InputNumber placeholder="Enter area in Sft" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="advance" label="Advance" rules={[{ required: true, message: 'Advance amount is required' }]}>
                  <InputNumber placeholder="Enter advance amount" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="size" label="Type of Commercial Property" rules={[{ required: true, message: 'Commercial type is required' }]}>
                  <Select placeholder="Select commercial type" style={{ width: '100%' }}>
                    <Select.Option value="Shop">Shop</Select.Option>
                    <Select.Option value="Office">Office</Select.Option>
                    <Select.Option value="Warehouse">Warehouse</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item label="Photos" required>
                  <Upload
                    listType="picture"
                    beforeUpload={() => false}
                    onChange={(info) => handleUploadChange(info, setCommercialImages)}
                    showUploadList={{ showPreviewIcon: true }}
                  >
                    <Button icon={<UploadOutlined />}>Add Images</Button>
                  </Upload>
                </Form.Item>
                <Form.Item name="nearbyFacilities" label="Details" rules={[{ required: true, message: 'Details are required' }]}>
                  <TextArea placeholder="Describe commercial property details" rows={4} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: '100%' }}>Submit</Button>
                </Form.Item>
              </Form>
            </TabPane>
          </Tabs>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default PropertyForm;
